import { useAuth } from "@/composables/useAuth";
import { useMaintenance } from "@/composables/useMaintenance";
import Session from "supertokens-web-js/recipe/session";

let errorToast = null;

/**
 * Parse response data based on content-type
 */
export async function afterFetch(ctx) {
    const isJson = ctx.response.headers?.get("content-type")?.includes("application/json");
    ctx.data = isJson ? await ctx.response.json() : await ctx.response.text();
    return ctx;
}

/**
 * Handle fetch errors with:
 * - Maintenance mode detection (503)
 * - Session refresh on 401/403
 * - User-friendly error toasts
 */
export async function onFetchError(ctx) {
    // Ignore aborted requests
    if (ctx.error.name === "AbortError") {
        return ctx;
    }

    // Handle maintenance mode (503 Service Unavailable)
    if (ctx.response.status === 503) {
        const isMaintenanceMode = detectMaintenanceMode(ctx.data);

        if (isMaintenanceMode) {
            const { setMaintenanceMode } = useMaintenance();
            setMaintenanceMode(true);
            window.location.href = "/maintenance";
            return ctx;
        }
    }

    // Handle authentication errors
    if ([401, 403].includes(ctx.response.status)) {
        await handleAuthError();
    }

    // Show error toast
    showErrorToast(ctx);

    return ctx;
}

/**
 * Check if a 503 response is maintenance mode
 */
export function detectMaintenanceMode(data) {
    if (typeof data === "object" && data?.code === "MAINTENANCE_MODE") {
        return true;
    }
    if (typeof data === "string" && data.includes("MAINTENANCE_MODE")) {
        return true;
    }
    return false;
}

/**
 * Try to refresh session, logout if failed
 */
export async function handleAuthError() {
    const { authStore, logout } = useAuth();

    if (authStore.isConnected) {
        const refreshed = await Session.attemptRefreshingSession();
        if (!refreshed) {
            logout();
        }
    }
}

/**
 * Display user-friendly error toast based on status code
 */
export function showErrorToast(ctx) {
    let error = ctx.data?.message || ctx.response.status;

    // Map status codes to French error messages
    if (ctx.response.status === 400) {
        error = "Veuillez vérifier les informations saisies";
    } else if (ctx.response.status === 413) {
        error = "L'image sélectionnée est trop volumineuse";
    } else if (ctx.response.status >= 500) {
        error = "Si l'erreur persiste, merci de contacter un administrateur";
    }

    // Handle validation error message
    if (error === "Validation failed") {
        error = "Veuillez vérifier les informations saisies";
    }

    // Clear previous toast and show new one
    errorToast?.clear();
    errorToast = toast.error({
        title: "Une erreur est survenue",
        message: error,
    });
}

/**
 * Configured fetch composable with error handling
 */
export const useFetch = createFetch({
    baseUrl: "/api",
    options: {
        afterFetch,
        onFetchError,
    },
    fetchOptions: {
        mode: "cors",
        credentials: "include",
    },
});
